// What buyers say about one product, in three tabs: the stars (how many gave
// each of five to one), the written reviews (paged, newest first) and why goods
// came back (a sortable table of reasons with how often). The product dialog's
// User Feedback tab, and the product a feedback table opens. Whoever may see a
// product's feedback (the company that sells it, an admin) may also answer a
// review or remove its words; the API holds the same line.
import React, { useEffect, useState } from "react"
import { StarIcon } from "@heroicons/react/24/solid"
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, TrashIcon, ChatBubbleLeftIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import Modal from "aldoo-ra/Modal"
import { showNotification } from "aldoo-ra/Notification"
import { FeedbackProduct, FeedbackComments, FeedbackReply, FeedbackDelete } from "../api"
import { TextArea, errorMessage } from "../ui"
import { buildContentUrl } from "../../Storefront/utils/contentRoutes"
import usePortalTexts from "../../Vendor/texts/usePortalTexts"
import { FEEDBACK } from "../../Vendor/texts/bundles"

/** Why goods came back (aldoo-na/store/returns REASONS), as the screens name them. */
export const REASON_LABELS = {
  undelivered: "Not collected or refused",
  withdrawal: "Changed their mind (withdrawal)",
  wrong_size: "Wrong size or fit",
  not_as_described: "Not as described",
  wrong_item: "Wrong item sent",
  defective: "Defective",
  damaged_in_transit: "Damaged in transit",
  other: "Other",
}

export function Stars({ value = 0, size = "w-4 h-4" }) {
  return (
    <span className="inline-flex items-center" aria-label={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <StarIcon key={star} className={`${size} ${value >= star - 0.25 ? "text-amber-400" : "text-admin_neutral_2"}`} />
      ))}
    </span>
  )
}

function Ratings({ ratings, t }) {
  const max = Math.max(1, ...Object.values(ratings.counts))
  return (
    <div className="flex flex-col sm:flex-row gap-6" data-testid="feedback-ratings">
      <div className="flex flex-col items-center justify-center min-w-[140px] rounded-lg border border-admin_bordered p-4">
        <div className="text-4xl font-bold text-admin_text">{ratings.total ? ratings.average.toFixed(1) : "—"}</div>
        <Stars value={ratings.average} size="w-5 h-5" />
        <div className="mt-1 text-xs text-admin_text_secondary">{t("%s ratings", ratings.total)}</div>
      </div>
      {/* the five stars always, a bar each: a star nobody gave shows as nothing given */}
      <div className="flex-1 flex flex-col gap-1.5 justify-center">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = ratings.counts[star] || 0
          return (
            <div key={star} className="flex items-center gap-3 text-sm" data-testid={`feedback-star-${star}`}>
              <span className="inline-flex items-center gap-1 w-10 shrink-0 text-admin_text_secondary">
                {star} <StarIcon className="w-3.5 h-3.5 text-amber-400" />
              </span>
              <span className="flex-1 h-2.5 rounded-full bg-admin_neutral_1 overflow-hidden">
                <span className="block h-full rounded-full bg-amber-400" style={{ width: `${(count / max) * 100}%` }} />
              </span>
              <span className="w-16 text-right tabular-nums">
                {count}
                <span className="ml-1 text-xs text-admin_text_secondary">{ratings.total ? `${Math.round((count / ratings.total) * 100)}%` : ""}</span>
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const failed = (message) => showNotification({ message, className: "bg-admin_error text-white" })

/**
 * Asks before something is removed: a dialog stacked over the feedback one.
 * `onConfirm` does it; the dialog stays, busy, until it is done.
 */
function ConfirmDelete({ t, message, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false)
  const confirm = async () => {
    setBusy(true)
    try {
      await onConfirm()
    } finally {
      setBusy(false)
      onClose()
    }
  }
  return (
    <Modal isOpen onClose={onClose} variant="admin_compact" title={t`Delete`} styleOverrides={{ container: "bg-admin_paper w-full max-w-[440px] rounded-md border border-admin_bordered shadow-lg" }}>
      <div className="p-4 flex flex-col gap-4" data-testid="feedback-confirm-delete">
        <div className="text-sm">{message}</div>
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose} disabled={busy}>
            {t`Cancel`}
          </Button>
          <Button variant="admin_primary_red" onClick={confirm} disabled={busy} data-testid="feedback-confirm-delete-yes">
            {t`Delete`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function DeleteButton({ label, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} title={label} aria-label={label} className="p-1 rounded text-admin_text_secondary hover:text-admin_error hover:bg-admin_neutral_1" data-testid={testId}>
      <TrashIcon className="w-4 h-4" />
    </button>
  )
}

/** One written review, the comments under it, and the seller's way to answer or remove it. */
function Review({ pid, review, t, locale, onRemoved, onChange }) {
  const [composing, setComposing] = useState(false)
  const [text, setText] = useState("")
  const [busy, setBusy] = useState(false)
  // what is about to be removed, asked first: { message, onConfirm }
  const [asking, setAsking] = useState(null)
  const confirmDelete = (message, onConfirm) => setAsking({ message, onConfirm })
  const dateOf = (value) => (value ? new Date(value).toLocaleDateString(locale, { dateStyle: "medium" }) : "")

  const send = async () => {
    const body = text.trim()
    if (!body || busy) return
    setBusy(true)
    try {
      const res = await FeedbackReply(pid, review.id, body)
      const error = errorMessage(res, t`Request failed`)
      if (error) return failed(error)
      onChange({ ...review, replies: [...review.replies, res] })
      setText("")
      setComposing(false)
    } catch (error) {
      failed(error.message)
    } finally {
      setBusy(false)
    }
  }

  const removeReview = () =>
    confirmDelete(t`Delete this review? Its words and the replies under it are removed; its stars stay in the rating.`, async () => {
      const res = await FeedbackDelete(pid, review.id).catch((error) => ({ error: error.message }))
      const error = errorMessage(res, t`Request failed`)
      if (error) return failed(error)
      onRemoved(review.id)
    })

  const removeReply = (reply) =>
    confirmDelete(t`Delete this reply?`, async () => {
      const res = await FeedbackDelete(pid, review.id, reply.id).catch((error) => ({ error: error.message }))
      const error = errorMessage(res, t`Request failed`)
      if (error) return failed(error)
      onChange({ ...review, replies: review.replies.filter((item) => item.id !== reply.id) })
    })

  return (
    <div className="rounded-lg border border-admin_bordered p-3 flex flex-col gap-1" data-testid="feedback-review">
      {asking && <ConfirmDelete t={t} message={asking.message} onConfirm={asking.onConfirm} onClose={() => setAsking(null)} />}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Stars value={review.rating} />
        <span className="font-medium">{review.author || t`A buyer`}</span>
        <span className="ml-auto text-xs text-admin_text_secondary">{dateOf(review.created)}</span>
        <DeleteButton label={t`Delete review`} onClick={removeReview} testId="feedback-review-delete" />
      </div>
      <div className="text-sm whitespace-pre-line">{review.text}</div>
      {(review.likes > 0 || review.flags > 0) && (
        <div className="text-xs text-admin_text_secondary">
          {review.likes > 0 && t("%s found it helpful", review.likes)}
          {review.likes > 0 && review.flags > 0 && " · "}
          {review.flags > 0 && <span className="text-admin_error">{t("%s flagged it", review.flags)}</span>}
        </div>
      )}

      {review.replies.length > 0 && (
        <div className="mt-2 ml-6 flex flex-col gap-2" data-testid="feedback-replies">
          {review.replies.map((reply) => (
            <div key={reply.id} className={`rounded-md px-3 py-2 text-sm ${reply.replyAs ? "bg-admin_primary/5 border-l-2 border-admin_primary" : "bg-admin_neutral_1/60"}`} data-testid="feedback-reply">
              <div className="flex items-center gap-2">
                <span className="font-medium">{reply.author || t`A buyer`}</span>
                {reply.replyAs && <span className="text-[11px] uppercase tracking-wide text-admin_primary">{reply.replyAs === "aldoo" ? t`Aldoo` : t`Seller`}</span>}
                <span className="ml-auto text-xs text-admin_text_secondary">{dateOf(reply.created)}</span>
                <DeleteButton label={t`Delete reply`} onClick={() => removeReply(reply)} testId="feedback-reply-delete" />
              </div>
              <div className="whitespace-pre-line">{reply.text}</div>
            </div>
          ))}
        </div>
      )}

      {!composing && (
        <button type="button" onClick={() => setComposing(true)} className="mt-1 self-start inline-flex items-center gap-1 text-sm text-admin_primary hover:underline" data-testid="feedback-reply-open">
          <ChatBubbleLeftIcon className="w-4 h-4" /> {t`Reply`}
        </button>
      )}
      {composing && (
        <div className="mt-2 ml-6 flex flex-col gap-2">
          <TextArea value={text} onChange={setText} rows={3} maxLength={4000} disabled={busy} autoFocus placeholder={t`Your reply, shown under the review on the product page`} data-testid="feedback-reply-text" />
          <div className="flex gap-2">
            <Button variant="admin_primary" onClick={send} disabled={busy || !text.trim()} data-testid="feedback-reply-send">
              {t`Send reply`}
            </Button>
            <Button variant="admin_secondary" onClick={() => setComposing(false)} disabled={busy}>
              {t`Cancel`}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function Comments({ pid, t, locale, onCount }) {
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    let alive = true
    setData(null)
    FeedbackComments(pid, page, 10).then((res) => alive && setData(res?.error ? { list: [], total: 0, pages: 0 } : res)).catch(() => alive && setData({ list: [], total: 0, pages: 0 }))
    return () => {
      alive = false
    }
  }, [pid, page, reload])
  if (!data) return <Spinner variant="admin_primary" size="md" />
  if (!data.list?.length) return <div className="text-sm italic text-admin_text_secondary py-4">{t`No written reviews yet.`}</div>

  const change = (review) => setData((current) => ({ ...current, list: current.list.map((item) => (item.id === review.id ? review : item)) }))
  // a removed review leaves the list; the page is fetched again so the next one moves up
  const removed = () => {
    onCount(data.total - 1)
    if (data.list.length === 1 && page > 1) return setPage(page - 1)
    setReload((n) => n + 1)
  }

  return (
    <div className="flex flex-col gap-3" data-testid="feedback-comments">
      {data.list.map((review) => (
        <Review key={review.id} pid={pid} review={{ ...review, replies: review.replies || [] }} t={t} locale={locale} onChange={change} onRemoved={removed} />
      ))}
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm" data-testid="feedback-comments-pages">
          <button type="button" className="px-3 h-8 rounded-md border border-admin_bordered disabled:opacity-40" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {t`Previous`}
          </button>
          <span className="tabular-nums">{t("Page %s of %s", page, data.pages)}</span>
          <button type="button" className="px-3 h-8 rounded-md border border-admin_bordered disabled:opacity-40" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>
            {t`Next`}
          </button>
        </div>
      )}
    </div>
  )
}

/** A column header that sorts the table: once descending, again ascending. */
export function SortHeader({ id, label, sort, onSort, align = "left" }) {
  const active = sort.key === id
  const Icon = !active ? ChevronUpDownIcon : sort.direction === "asc" ? ChevronUpIcon : ChevronDownIcon
  return (
    <th className={`py-2 px-2 font-semibold ${align === "right" ? "text-right" : "text-left"}`}>
      <button type="button" onClick={() => onSort(id)} className={`inline-flex items-center gap-1 uppercase text-[11px] tracking-wide ${active ? "text-admin_text" : "text-admin_text_secondary"} hover:text-admin_text`} data-testid={`sort-${id}`}>
        {label}
        <Icon className="w-3.5 h-3.5" />
      </button>
    </th>
  )
}

function Reasons({ reasons, t }) {
  const [sort, setSort] = useState({ key: "requests", direction: "desc" })
  const total = reasons.reduce((sum, row) => sum + row.requests + row.returns, 0)
  const onSort = (key) => setSort((current) => ({ key, direction: current.key === key && current.direction === "desc" ? "asc" : "desc" }))
  const rows = [...reasons]
    .map((row) => ({ ...row, label: t(REASON_LABELS[row.reason] || row.reason), share: total ? (row.requests + row.returns) / total : 0 }))
    .sort((a, b) => {
      const x = sort.key === "reason" ? a.label : a[sort.key]
      const y = sort.key === "reason" ? b.label : b[sort.key]
      return (x === y ? 0 : x > y ? 1 : -1) * (sort.direction === "asc" ? 1 : -1)
    })
  if (!rows.length) return <div className="text-sm italic text-admin_text_secondary py-4">{t`Nothing of this product has been returned.`}</div>
  return (
    <table className="w-full text-sm" data-testid="feedback-reasons">
      <thead className="border-b border-admin_bordered">
        <tr>
          <SortHeader id="reason" label={t`Reason`} sort={sort} onSort={onSort} />
          <SortHeader id="requests" label={t`Return requests`} sort={sort} onSort={onSort} align="right" />
          <SortHeader id="returns" label={t`Returns`} sort={sort} onSort={onSort} align="right" />
          <SortHeader id="units" label={t`Units returned`} sort={sort} onSort={onSort} align="right" />
          <SortHeader id="share" label={t`Share`} sort={sort} onSort={onSort} align="right" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.reason} className="border-b border-admin_bordered/60" data-testid="feedback-reason-row">
            <td className="py-2 px-2">{row.label}</td>
            <td className="py-2 px-2 text-right tabular-nums">{row.requests}</td>
            <td className="py-2 px-2 text-right tabular-nums">{row.returns}</td>
            <td className="py-2 px-2 text-right tabular-nums">{row.units}</td>
            <td className="py-2 px-2 text-right tabular-nums">{Math.round(row.share * 100)}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const TABS = [
  ["ratings", "Ratings"],
  ["comments", "Comments"],
  ["reasons", "Return reasons"],
]

/**
 * One product's feedback: `pid` names it; `locale` dates the reviews and
 * `language` names the product. `onProduct({ name, thumbnail, url })` hears what the
 * product is once it has loaded (a dialog's title).
 */
export default function ProductFeedback({ pid, locale = "bg-BG", language, onProduct }) {
  const t = usePortalTexts(FEEDBACK)
  const [tab, setTab] = useState("ratings")
  const [data, setData] = useState(null)
  useEffect(() => {
    if (!pid) return
    let alive = true
    setData(null)
    FeedbackProduct(pid, language)
      .then((res) => {
        if (!alive) return
        setData(res?.error ? { error: res.message || res.error } : res)
        if (!res?.error) onProduct?.({ name: res.name, thumbnail: res.thumbnail, url: buildContentUrl({ pid: res.pid, profile: res.profile }) })
      })
      .catch((error) => alive && setData({ error: error.message }))
    return () => {
      alive = false
    }
  }, [pid, language])
  if (!pid) return null
  if (!data) return <Spinner variant="admin_primary" size="md" />
  if (data.error) return <div className="text-sm text-admin_error">{data.error}</div>
  const counts = { ratings: data.ratings?.total || 0, comments: data.comments || 0, reasons: (data.reasons || []).reduce((sum, row) => sum + row.requests + row.returns, 0) }
  return (
    <div className="flex flex-col gap-4" data-testid="product-feedback">
      <div className="flex gap-2 border-b border-admin_bordered" role="tablist">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px px-3 pb-2 pt-1 border-b-2 text-sm font-semibold ${tab === id ? "border-admin_primary text-admin_text" : "border-transparent text-admin_text_secondary hover:text-admin_text"}`}
            data-testid={`feedback-tab-${id}`}
          >
            {t(label)} <span className="ml-1 text-xs font-normal text-admin_text_secondary">{counts[id]}</span>
          </button>
        ))}
      </div>
      {tab === "ratings" && <Ratings ratings={data.ratings} t={t} />}
      {tab === "comments" && <Comments pid={pid} t={t} locale={locale} onCount={(comments) => setData((current) => ({ ...current, comments }))} />}
      {tab === "reasons" && <Reasons reasons={data.reasons || []} t={t} />}
    </div>
  )
}
